import React, { useEffect, useRef, FC } from 'react';
import { connect } from 'react-redux';
import APP_STATUS from '@integrations/pint-appstore-commons/src/enums/APP_STATUS.json';
import { fetchStatusFrom, openControlCenter, updateCoeditPolicy } from '../common/appStatusActionCreators';
import ControlCenterItem from './ControlCenterItem';
import t from '../../translations';
import { AppId, CollaborationAppsPrioritizationData, Editor, ExtendedRootState } from '../common/interfaces';
import { updateEditorsOrder, saveNewCollaborationAppsHierarchy } from '../editors/editorsCreators';
import CoeditingSettings from './CoeditingSettings';
import { CoeditPolicy } from '../editors/editorsReducer';
import { track } from '../tracking/trackMiddleware';

interface ControlCenterProps {
    dispatchFetchStatusForm: (appId: string) => void;
    editorsApps: Editor[];
    coeditPolicy: CoeditPolicy;
    dispatchUpdateCoeditPolicy: (coeditPolicy: CoeditPolicy) => void;
    dispatchOpenControlCenter: () => void;
    collaborationAppsHierarchy: AppId[];
    collaborationAppsPrioritizationEnabled: boolean;
    dispatchUpdateEditorsOrder: (editorsOrder: Editor[]) => void;
    editorsOrder: Editor[];
    dispatchSaveNewCollaborationAppsHierarchy: ({
        collaborationAppsPrioritizationEnabled,
        collaborationAppsHierarchy,
    }: CollaborationAppsPrioritizationData) => void;
}

const ControlCenter: FC<ControlCenterProps> = ({
    dispatchFetchStatusForm,
    editorsApps,
    coeditPolicy,
    dispatchOpenControlCenter,
    dispatchUpdateCoeditPolicy,
    collaborationAppsHierarchy,
    collaborationAppsPrioritizationEnabled,
    dispatchUpdateEditorsOrder,
    editorsOrder,
    dispatchSaveNewCollaborationAppsHierarchy,
}) => {
    useEffect(() => {
        dispatchOpenControlCenter();
    }, []);

    useEffect(() => {
        dispatchUpdateEditorsOrder(sortEditorApps(editorsApps, collaborationAppsHierarchy));
    }, [editorsApps, collaborationAppsHierarchy]);

    const editorsOrderRef: React.MutableRefObject<Editor[]> = useRef([]);
    editorsOrderRef.current = [...editorsOrder];

    const findEditor = (appId: string) => {
        const editor = editorsOrderRef.current.filter((editor) => editor.appId === appId)[0];

        return {
            editor,
            index: editorsOrderRef.current.indexOf(editor),
        };
    };

    const moveEditor = (appId: string, atIndex: number) => {
        const { editor, index } = findEditor(appId);
        const editorsUpdatedOrder = [...editorsOrderRef.current];
        editorsUpdatedOrder.splice(index, 1);
        editorsUpdatedOrder.splice(atIndex, 0, editor);

        if (atIndex === 0) {
            track('enable_app', { appId, wasTheAppSetAsDefault: true });
        } else if (index === 0) {
            track('enable_app', { appId: editorsUpdatedOrder[0].appId, wasTheAppSetAsDefault: true });
        }
        dispatchUpdateEditorsOrder(editorsUpdatedOrder);
    };

    const editorsPrioritizationToggleChangeHandler = () => {
        dispatchSaveNewCollaborationAppsHierarchy(
            collaborationAppsPrioritizationEnabled
                ? {
                      collaborationAppsPrioritizationEnabled: false,
                      collaborationAppsHierarchy: [],
                  }
                : {
                      collaborationAppsPrioritizationEnabled: true,
                      collaborationAppsHierarchy: mapEditorsOrderToAppsHierarchy(editorsOrder),
                  }
        );
    };

    const editorsPriorityChangeSubmitHandler = () => {
        dispatchSaveNewCollaborationAppsHierarchy({
            collaborationAppsPrioritizationEnabled,
            collaborationAppsHierarchy: mapEditorsOrderToAppsHierarchy(editorsOrder),
        });
    };
    const allEditorsDisabled = editorsApps.every((e) => e.status === APP_STATUS.DISABLED);

    const isMSDesktopEnabled = editorsApps
        .filter((editor) => editor.appId === 'mscollabdesktop' || editor.appId === 'mswopibeta')
        .some((editor) => editor.status !== APP_STATUS.DISABLED);

    return (
        <div className="control-center">
            <h2>{t.controlCenter.title}</h2>
            <div className="control-center__app-list">
                <h3>{t.controlCenter.header}</h3>
                <p>{t.controlCenter.description}</p>
                <br />
                <div className="control-center__prioritization-management-section">
                    <span
                        data-tooltip={t.controlCenter.prioritizationToggleTooltip}
                        className={`control-center__prioritization-toggle-tooltip ${
                            allEditorsDisabled ? '' : 'control-center__prioritization-toggle-tooltip--disabled'
                        }`}
                    >
                        <span
                            className={`sg-toggle ${
                                allEditorsDisabled ? 'control-center__prioritization-toggle__inactive' : ''
                            } ${collaborationAppsPrioritizationEnabled ? 'sg-toggle--on' : ''}`}
                            /* eslint-disable-next-line @typescript-eslint/no-empty-function */
                            onClick={allEditorsDisabled ? () => {} : editorsPrioritizationToggleChangeHandler}
                        />{' '}
                    </span>
                    <span className="control-center__prioritization-management-labels">
                        <span>{t.controlCenter.prioritizationCaption}</span>
                        <span>{t.controlCenter.prioritizationExplanation}</span>
                    </span>
                </div>
                <div className="control-center__panel">
                    <div
                        className={`control-center__panel-header ${
                            collaborationAppsPrioritizationEnabled
                                ? ''
                                : 'control-center__panel-header--prioritization-disabled'
                        }`}
                    >
                        {collaborationAppsPrioritizationEnabled && <p />}
                        <p>{t.controlCenter.panelHeaderName}</p>
                        <p>{t.controlCenter.panelHeaderStatus}</p>
                    </div>
                    <div>
                        {editorsOrder.map((app) => (
                            <ControlCenterItem
                                app={app}
                                fetchStatusForm={dispatchFetchStatusForm}
                                key={app.appId}
                                moveEditor={moveEditor}
                                findEditor={findEditor}
                                prioritizationEnabled={collaborationAppsPrioritizationEnabled}
                                submitPriorityUpdate={editorsPriorityChangeSubmitHandler}
                            />
                        ))}
                    </div>
                </div>
            </div>
            {!!editorsOrder.length && isMSDesktopEnabled && (
                <CoeditingSettings
                    onChange={dispatchUpdateCoeditPolicy}
                    coeditPolicy={coeditPolicy}
                    collaborationAppsPrioritizationEnabled={collaborationAppsPrioritizationEnabled}
                />
            )}
        </div>
    );
};

function mapEditorsOrderToAppsHierarchy(editorsOrder: Editor[]): AppId[] {
    return editorsOrder.filter((e) => e.status !== APP_STATUS.DISABLED).map((e) => e.appId);
}

function sortEditorApps(editorsApps: Editor[], collaborationAppsHierarchy: AppId[] = []): Editor[] {
    let editors = [...editorsApps];
    const editorsSorted: Editor[] = [];
    /* eslint-disable-next-line no-restricted-syntax */
    for (const appId of collaborationAppsHierarchy) {
        const editor = editors.find((editor) => editor.appId === appId) as Editor;
        editorsSorted.push({ ...editor });
        editors = editors.filter((editor) => editor.appId !== appId);
    }

    editors.sort((app) => (app.status === 'DISABLED' ? 1 : -1));

    return editorsSorted.concat(editors);
}

const mapDispatchToProps = {
    dispatchUpdateCoeditPolicy: updateCoeditPolicy,
    dispatchFetchStatusForm: fetchStatusFrom,
    dispatchOpenControlCenter: openControlCenter,
    dispatchUpdateEditorsOrder: updateEditorsOrder,
    dispatchSaveNewCollaborationAppsHierarchy: saveNewCollaborationAppsHierarchy,
};

export default connect((state) => {
    const { editors = {} } = state as ExtendedRootState;
    const {
        editorsApps = [],
        coeditPolicy = {},
        collaborationAppsHierarchy = [],
        collaborationAppsPrioritizationEnabled = false,
        stateUpdate: { editorsOrder = [] },
    } = editors;

    return {
        coeditPolicy,
        editorsApps,
        collaborationAppsHierarchy,
        collaborationAppsPrioritizationEnabled,
        editorsOrder,
    };
}, mapDispatchToProps)(ControlCenter);
